// @flow
import * as React from 'react';
import styled from 'styled-components';
import { sprintf } from 'sprintf-js';
import { trackClicked } from 'utils/analytics';
import { algoliaTrackClick } from 'utils/algoliaSearch';
import { isNotEmpty } from 'utils/functional';
import type { Subsite } from 'api/website';
import { fetchTag, fetchAuthor } from 'api/website';
import Box from 'components/Box';
import Icon, { AnimatedCheckmark } from 'components/Icon';
import Pagination from 'components/Pagination';
import { FilestackImage } from 'components/Image';
import Divider from 'components/Divider';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import SearchEmptyResultsMessage from 'components/SearchEmptyResultsMessage';
import { Link as RouterLink } from 'components/RouterLink';
import { SecondaryButton } from 'components/Button';
import PlaceholderImage from 'components/PlaceholderImage';
import PostSearchFiltersSidebarContainer from 'containers/PostSearchFiltersSidebarContainer';
import PostSearchInputContainer from 'containers/PostSearchInputContainer';
import SubscribeToSubsiteContainer from 'containers/SubscribeToSubsiteContainer';
import scrollIntoView from 'utils/scrollIntoView';
import Byline from 'components/Byline';
import Text, { H1, H2, H3, BodySmall, HeadingSmall } from 'components/Text';
import { gutterWidth, colors, transition, fontWeights } from '../theme';

const { useState, useEffect } = React;

type PostHit = {
  author: ?{
    imageHandle: ?string,
    name: string,
    slug: string
  },
  categories: Array<string>,
  content: string,
  headerImage: ?{ handle: string, mimetype: string },
  locale: UserLocale,
  objectID: string,
  postedDate: string,
  published: number,
  showDate: boolean,
  subsiteId: Array<string>,
  subsiteName: string,
  tags: Array<string>,
  thumbnail: ?{ handle: string, mimetype: string },
  title: string,
  url: string,
  orgID: string,
  type: string,
  metaDescription: ?string
};

const isFiltered = (facet: string | Array<string>) =>
  facet instanceof Array
    ? !(facet.length === 1 && facet[0] === 'DUMMY_OPTION')
    : facet !== 'ALL';

const buildImageData = data =>
  data
    ? {
        handle: data.handle,
        size: 0,
        mimetype: data.mimetype,
        filename: 'file.jpg',
        source: 'local'
      }
    : null;

const FeaturedImage = styled(Box)`
  -webkit-mask-image: -webkit-radial-gradient(white, black);
  img {
    width: 100%;
    height: auto;
  }
`;

const ImageLink = styled(RouterLink)`
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const PostLink = styled(Box)`
  color: ${colors.greyishBrown};
  text-decoration: none;
  transition: all ${transition};
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
    text-decoration: none;
  }
`;

const CategoryLabel = props => (
  <HeadingSmall
    mt={24}
    mb="4px"
    color={colors.silver}
    fontSize={14}
    letterSpacing="1.4px"
    {...props}
  />
);

const PostSearchImage = ({
  post,
  subsite,
  featured
}: {
  post: PostHit,
  subsite: Subsite,
  featured: boolean
}) =>
  post.thumbnail || post.headerImage ? (
    <FilestackImage
      width={featured ? 736 : 344}
      height={featured ? 440 : 206}
      data={buildImageData(post.thumbnail || post.headerImage)}
      fit="crop"
      alt={post.title}
    />
  ) : isNotEmpty(subsite.placeholderImages) ? (
    <PlaceholderImage
      postId={post.objectID}
      subsite={subsite}
      width={featured ? 736 : 344}
      height={featured ? 440 : 206}
      fit="crop"
      alt={post.title}
    />
  ) : (
    <Box position="relative" bg={colors.aluminum}>
      <Box pb={`${(206 / 344) * 100}%`} />
      <Box
        position="absolute"
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
      >
        <Text fontWeight={fontWeights.bold} fontSize={96} color={colors.silver}>
          {post.title.slice(0, 1).toUpperCase()}
        </Text>
      </Box>
    </Box>
  );

const PostCard = ({
  post,
  subsite,
  homePageUrl,
  featured = false,
  categories,
  index,
  queryID,
  hitsPerPage,
  page,
  ...props
}: {
  post: PostHit,
  subsite: Subsite,
  homePageUrl: string,
  featured?: boolean,
  categories: Object,
  index: number,
  queryID: string,
  hitsPerPage: number,
  page: number
}) => {
  const Title = featured ? H2 : H3;
  const track = () => {
    algoliaTrackClick(
      queryID,
      post.objectID,
      // Position in algolia analytics is 1-indexed
      hitsPerPage ? page * hitsPerPage + 1 : 1
    );
    trackClicked('Search Result', {
      result_id: post.objectID,
      result_index: 0,
      result_type: post.type
      // TODO: add tracking data
      // ...getSearchTrackingData()
    });
  };
  const description = post.metaDescription;
  const truncatedDescription =
    description && description.length > 144
      ? [
          description.slice(0, 144).replace(/\.$/, ''),
          description.slice(144).split(' ')[0],
          description.slice(144).split(' ').length > 1 ? '&hellip;' : ''
        ].join('')
      : description;
  const postSubsite =
    subsite.includedSubsites && subsite.includedSubsites.length
      ? subsite.includedSubsites.find(s => post.subsiteId.includes(s.id))
      : null;
  return (
    <Box {...props}>
      <ImageLink
        to={{
          pathname: `/${post.url}`,
          state: {
            subsite
          }
        }}
        onClick={track}
        tabIndex="-1"
      >
        <FeaturedImage
          overflow="hidden"
          mx={featured && [-gutterWidth / 2, 0]}
          borderRadius={featured ? [0, 24] : 10}
        >
          <PostSearchImage subsite={subsite} post={post} featured={featured} />
        </FeaturedImage>
      </ImageLink>
      {postSubsite ? (
        <CategoryLabel>{postSubsite.publicName}</CategoryLabel>
      ) : (
        post.categories && (
          <CategoryLabel>
            {post.categories
              .map(category => categories[category] || '')
              .join(', ')}
          </CategoryLabel>
        )
      )}
      <Title mt={post.categories ? 0 : 24} mb={10}>
        <PostLink
          onClick={track}
          data-qa-id="search-result-link"
          component={RouterLink}
          to={{
            pathname: `/${post.url}`,
            state: {
              subsite
            }
          }}
        >
          {post.title}
        </PostLink>
      </Title>
      {featured && description && (
        <BodySmall
          mb={featured ? 24 : 12}
          dangerouslySetInnerHTML={{ __html: truncatedDescription }}
        />
      )}
      {(post.author || post.postedDate) && (
        <Box mt={featured ? 24 : 12}>
          <Byline
            slug={post.author ? post.author.slug : undefined}
            name={post.author ? post.author.name : undefined}
            imageHandle={
              post.author && post.author.imageHandle && featured
                ? post.author.imageHandle
                : undefined
            }
            homePageUrl={homePageUrl}
            postedDate={post.postedDate}
            showDate={post.showDate}
            small
          />
        </Box>
      )}
    </Box>
  );
};

type Props = {
  search: Object,
  scrollToId: string,
  homePageUrl: string,
  subsite: Subsite,
  parentSubsite: ?Subsite,
  locale: string
};

const PostSearchResults = ({
  search,
  homePageUrl,
  subsite,
  parentSubsite,
  locale
}: Props) => {
  const {
    pageIndex,
    resultsByPage,
    queryID,
    isSearching,
    initialized,
    filters,
    query
  } = search;
  const [tag, setTag] = useState(null);
  const [author, setAuthor] = useState(null);

  const results = resultsByPage[pageIndex.toString()];
  const { hits, nbHits, hitsPerPage, page, nbPages } = results || {};
  const optionTitles =
    search.searchFacets.articleCategoriesFacet &&
    search.searchFacets.articleCategoriesFacet.optionTitles;

  const searchingAuthors = isFiltered(filters.articleAuthorFacet);
  const searchingTags = isFiltered(filters.articleTagsFacet);

  useEffect(
    () => {
      if (searchingTags) {
        const slug = filters.articleTagsFacet[0];
        fetchTag(slug, subsite.id).then(res => setTag(res));
      } else {
        setTag(null);
      }
    },
    [filters.articleTagsFacet, locale, searchingTags, subsite.id]
  );

  useEffect(
    () => {
      if (searchingAuthors) {
        const slug = filters.articleAuthorFacet;
        fetchAuthor(slug).then(res => setAuthor(res));
      } else {
        setAuthor(null);
      }
    },
    [filters.articleAuthorFacet, locale, searchingAuthors, subsite.id]
  );

  const isLandingPage =
    pageIndex === 0 &&
    !((query && query.length) || searchingAuthors || searchingTags);

  const SearchResultsBody = () =>
    hits && hits.length > 0 && queryID ? (
      <>
        <Box pb={16} mb={16}>
          <BodySmall>
            {sprintf(getText('%(first)s-%(last)s of %(total)s %(word)s'), {
              first: (page * hitsPerPage + 1).toLocaleString(),
              last: Math.min((page + 1) * hitsPerPage, nbHits).toLocaleString(),
              total: nbHits.toLocaleString(),
              word: hits.length > 1 ? getText('articles') : getText('article')
            })}
          </BodySmall>
        </Box>
        {isLandingPage && (
          <>
            <PostCard
              post={hits[0]}
              homePageUrl={homePageUrl}
              featured
              categories={optionTitles}
              index={0}
              hitsPerPage={hitsPerPage}
              page={page}
              queryID={queryID}
              subsite={subsite}
              data-qa-id="search-result"
            />

            {hits.length > 1 && (
              <Divider
                light
                mt={[24, null, null, 48]}
                mb={[24, null, null, 0]}
              />
            )}
          </>
        )}
        <Box
          mt={
            isLandingPage
              ? [gutterWidth / 2, null, null, (gutterWidth * 3) / 2]
              : -24
          }
          mx={-24}
          display="flex"
          flexWrap="wrap"
        >
          {hits.slice(isLandingPage ? 1 : 0).map((hit, index) => (
            <Box p={24} key={hit.objectID} width={[1, 1 / 2]} flex="0 0 auto">
              <PostCard
                height="100%"
                post={hit}
                homePageUrl={homePageUrl}
                categories={optionTitles}
                index={index}
                hitsPerPage={hitsPerPage}
                page={page}
                queryID={queryID}
                subsite={subsite}
                data-qa-id="search-result"
              />
            </Box>
          ))}
        </Box>
        {typeof nbPages === 'number' &&
          typeof page === 'number' &&
          typeof hitsPerPage === 'number' && (
            <Pagination
              nbHits={nbHits}
              nbPages={nbPages}
              pageIndex={page}
              hitsPerPage={hitsPerPage}
              onClick={() => scrollIntoView()}
            />
          )}
      </>
    ) : isSearching || !initialized ? (
      <LoadingIndicator />
    ) : (
      <SearchEmptyResultsMessage
        resultType="articles"
        clearAll={() => null}
        noMessage
      />
    );

  return (
    <Box
      id="results"
      mx="auto"
      pt={[0, null, null, 48]}
      display="flex"
      flexWrap="wrap"
    >
      <Box
        data-qa-id="search-sidebar"
        width={[1, 1, 1, 1 / 3]}
        px={[gutterWidth / 2, null, null, gutterWidth]}
      >
        <Box data-qa-id="search-results-title">
          {parentSubsite && parentSubsite.homePage && (
            <Box
              data-qa-id="back-to-subsite-link"
              component={RouterLink}
              to={{
                pathname: `/${parentSubsite.homePage.url}`,
                state: { parentSubsite: null },
                search: 'pq=&searchMode=true'
              }}
              display="flex"
              mb={20}
              textDecoration="none"
            >
              <Box mr="6px" mt="6px">
                <Icon
                  name="chevron"
                  direction="left"
                  display="block"
                  width="10px"
                  fill="currentColor"
                  stroke="currentColor"
                />
              </Box>
              <Text styleAs="headingSmall" fontWeight="semiBold">
                {sprintf(getText('Back to %(name)s'), {
                  name: parentSubsite.publicName || parentSubsite.name
                })}
              </Text>
            </Box>
          )}

          <H1 mt={0}>{subsite.publicName || subsite.name}</H1>
        </Box>
        {(subsite.canSubscribe ||
          (subsite.searchLinks && subsite.searchLinks.length > 0)) && (
          <>
            <Box display="flex" alignItems="center" m={-12}>
              {subsite.canSubscribe && (
                <Box m={12}>
                  <SubscribeToSubsiteContainer
                    subsiteId={subsite.id}
                    render={({ subscribe, isSubscribed }) => (
                      <SecondaryButton
                        onClick={subscribe}
                        data-qa-id="subscribe-to-subsite"
                        square
                        blue
                        size="large"
                        disabled={isSubscribed}
                        px={
                          subsite.searchLinks && subsite.searchLinks.length > 1
                            ? '16px !important'
                            : undefined
                        }
                      >
                        <Box display="flex" alignItems="center">
                          {isSubscribed && (
                            <AnimatedCheckmark
                              stroke="currentColor"
                              width={16}
                              height={16}
                              mr="8px"
                            />
                          )}
                          <BodySmall styleAs="h5" letterSpacing={0}>
                            {isSubscribed
                              ? getText('Subscribed!')
                              : getText('Subscribe')}
                          </BodySmall>
                        </Box>
                      </SecondaryButton>
                    )}
                  />
                </Box>
              )}
              {subsite.searchLinks.map(searchLink => (
                <Box m={12} key={searchLink.data.url}>
                  <Link href={searchLink.data.url}>
                    <Text styleAs="h5" lineHeight={1} textAlign="center">
                      {searchLink.text}
                    </Text>
                  </Link>
                </Box>
              ))}
            </Box>
            <Divider light mt={36} mb={24} />
          </>
        )}
        <Box position="relative">
          <PostSearchInputContainer
            homePageUrl={homePageUrl}
            isStatic
            rerunSearch={() => null}
            pill={
              query && query.length
                ? query
                : author
                ? author.name
                : tag
                ? tag.tag
                : undefined
            }
          />
        </Box>
        <Box>
          <PostSearchFiltersSidebarContainer
            subsite={subsite}
            parentSubsite={parentSubsite}
          />
        </Box>
      </Box>
      <Box
        width={[1, 1, 1, 2 / 3]}
        px={[gutterWidth / 2, null, null, gutterWidth]}
        pt={[gutterWidth, null, null, 0]}
        data-qa-id="search-results"
      >
        <SearchResultsBody />
      </Box>
    </Box>
  );
};

export default PostSearchResults;
